import React, { useEffect, useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { FormField } from '../../components/ui/FormField';
import { useToast } from '../../components/ui/Toast';
import { Plus, Users } from 'lucide-react';
import api from '../../services/api';

interface User {
  id: string;
  keycloakId: string;
  name: string;
  mobile: string;
  role: 'admin' | 'agent';
  status: 'active' | 'inactive';
  createdAt: string;
}

interface InviteForm { name: string; mobile: string; role: 'admin' | 'agent' }

export const UserManagementPage: React.FC = () => {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState<InviteForm>({ name: '', mobile: '', role: 'agent' });

  const load = () => {
    setLoading(true);
    api.get<User[]>('/users')
      .then((r) => setUsers(r.data))
      .catch(() => showToast('Failed to load users', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post('/users', form);
      showToast('User created', 'success');
      setShowInvite(false);
      setForm({ name: '', mobile: '', role: 'agent' });
      load();
    } catch {
      showToast('Failed to create user', 'error');
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (user: User, role: 'admin' | 'agent') => {
    try {
      await api.patch(`/users/${user.id}/role`, { role });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, role } : u));
    } catch {
      showToast('Failed to update role', 'error');
    }
  };

  const toggleStatus = async (user: User) => {
    const endpoint = user.status === 'active' ? 'deactivate' : 'activate';
    try {
      await api.patch(`/users/${user.id}/${endpoint}`);
      setUsers((prev) => prev.map((u) => u.id === user.id
        ? { ...u, status: endpoint === 'deactivate' ? 'inactive' : 'active' }
        : u));
    } catch {
      showToast('Failed to update user status', 'error');
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="User Management" subtitle="Manage agents and administrators" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        <div className="page-header">
          <div>
            <div className="page-title">User Management</div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
              {loading ? 'Loading…' : `${users.length} user${users.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <button className="btn-primary" onClick={() => setShowInvite(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={15} />
            Invite User
          </button>
        </div>

        <div className="card">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="skeleton" style={{ height: '14px', width: '160px', margin: '0 auto' }} />
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Users size={22} /></div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '4px' }}>No users yet</div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>Invite the first user to get started.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Name</th><th>Mobile</th><th>Role</th><th>Status</th><th style={{ width: '120px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-1)' }}>{user.name}</td>
                      <td style={{ color: 'var(--text-2)' }}>{user.mobile}</td>
                      <td>
                        <select
                          value={user.role}
                          onChange={(e) => changeRole(user, e.target.value as 'admin' | 'agent')}
                          className="inp"
                          style={{ width: 'auto', padding: '4px 8px', fontSize: '12.5px' }}
                        >
                          <option value="agent">Agent</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td><Badge label={user.status === 'active' ? 'Active' : 'Inactive'} /></td>
                      <td>
                        <button
                          onClick={() => toggleStatus(user)}
                          className={user.status === 'active' ? 'btn-ghost btn-xs' : 'btn-ghost btn-xs'}
                          style={{ color: user.status === 'active' ? 'var(--err)' : 'var(--succ)', borderColor: user.status === 'active' ? 'var(--err-border)' : 'var(--succ-bg)' }}
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Invite User" size="sm">
        <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <FormField label="Full Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Ahmed Al-Rashid" />
          <FormField label="Mobile" required value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="05XXXXXXXX" />
          <div>
            <label className="lbl">Role</label>
            <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as 'admin' | 'agent' }))} className="inp">
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
            <button type="button" className="btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={inviting}>{inviting ? 'Creating…' : 'Create User'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
